import { useAutoAnimate } from "@formkit/auto-animate/react";
import { LockIcon, StackIcon, WifiIcon } from "@xola/icons";
import { Badge } from "@xola/ui-kit";
import React, { ReactElement } from "react";
import { useHistory } from "react-router-dom";
import { Experience, ExperienceStatus, Seller } from "types";
import { useSeller } from "../../hooks/useAccount";
import { makeExperiencePurchaseItem } from "../../models/CreatePurchaseState";
import { getDefaultPrice, hasPrivateOption } from "../../models/Experience";
import { CreatePurchaseStep, useCreatePurchaseStore } from "../../stores/createPurchaseStore";
import { sortBy } from "../../utils/locale";
import { Currency } from "../Common/Currency";
import { NoResults } from "../Common/NoResults";
import { NoTranslate } from "../Common/NoTranslate";
import { ProductImage } from "../Common/ProductImage";
import { SellerLogo } from "../Common/SellerLogo";
import { invalidateArrivalCache } from "../DatePicker/useArrivalTimeslots";
import { usePartnerExperiences } from "@/hooks/resources/useProduct";
import { useResource } from "@/hooks/useResource";
import { useSearch } from "@/hooks/useSearch";
import { ProductSkeleton } from "@/pages/Product/SelectExperiencePage";

export const PartnerExperienceList = ({ search }: { search: string }) => {
    const [animationParent] = useAutoAnimate<HTMLDivElement>();

    const { data: partnerExperiences, isLoading: isLoadingPartnerExperiences } = usePartnerExperiences();

    const validExperiences = partnerExperiences.filter(
        (experience) => experience.status === ExperienceStatus.PUBLISHED,
    );
    const sortedExperiences = sortBy(validExperiences, "name", false);
    const filteredExperiences = useSearch(
        sortedExperiences,
        (experience) => [experience.name?.trim(), experience.seller.name?.trim() ?? experience.seller.id ?? ""],
        search || "",
    );

    if (isLoadingPartnerExperiences) {
        return <ProductSkeleton header="Partner Experiences" />;
    }

    return (
        <div className="px-0 py-10">
            <div className="text-lg font-bold">Partner Experiences</div>
            {filteredExperiences.length === 0 ? (
                <NoResults>No results found</NoResults>
            ) : (
                <div
                    ref={animationParent}
                    className="mt-10 grid grid-cols-3 gap-y-5 gap-x-2"
                    data-testid="experience-list"
                >
                    {filteredExperiences.map((experience) => (
                        <ExperienceListItem key={experience.id} experience={experience} />
                    ))}
                </div>
            )}
        </div>
    );
};

const ExperienceListItem = ({ experience }: { experience: Experience }) => {
    const history = useHistory<{ isAddingItem?: boolean } | null>();
    const seller = useSeller();

    const { data: partnerSeller } = useResource<Seller>(["users", experience.seller.id]);

    const {
        actions: { resetState, addItem, setCurrency, setSeller, setPartnerSeller },
        isMist: hasAnyItems,
    } = useCreatePurchaseStore();

    const handleExperienceSelect = () => {
        resetState();
        invalidateArrivalCache();
        setSeller(experience.seller.id);
        setPartnerSeller(seller.id);
        addItem(makeExperiencePurchaseItem(experience));
        setCurrency(seller.currency);
        useCreatePurchaseStore.setState({
            /** If user already has any items in the cart, and tries to add a new experience, it would be a MIST checkout. So rather than redirecting him to `CHECKOUT` step, we'll redirect to `SELECT_DATE` */
            step: hasAnyItems() ? CreatePurchaseStep.SELECT_DATE : CreatePurchaseStep.CHECKOUT,
        });
        history.push("/cart");
    };

    return <ExperienceTile experience={experience} seller={partnerSeller} onClick={handleExperienceSelect} />;
};

export const ExperienceTile = ({
    experience,
    seller,
    onClick,
}: {
    experience: Experience;
    seller?: Seller;
    onClick: () => void;
}) => {
    const price = getDefaultPrice(experience);
    const isVirtual = experience.virtualMeeting?.enabled ?? false;

    return (
        <div
            className="flex cursor-pointer flex-row rounded py-2 hover:bg-secondary-hover"
            data-testid="experience-list-item"
            onClick={onClick}
        >
            <ProductImage product={experience} />
            <div className="flex flex-col space-y-1">
                <NoTranslate className="text-md font-bold">{experience.name}</NoTranslate>

                {seller && (
                    <div className="flex">
                        <ProductBadge
                            text={seller.name}
                            icon={<SellerLogo seller={seller} size="small" />}
                            color="primary"
                        />
                    </div>
                )}

                <div className="space-x-2">
                    <ProductBadge text={getPriceBadgeText(price)} icon={<StackIcon />} />
                    {isVirtual && <ProductBadge color="warning" text="Online Experience" icon={<WifiIcon />} />}
                    {hasPrivateOption(experience) && (
                        <ProductBadge color="warning" text="Private" icon={<LockIcon />} />
                    )}
                </div>
            </div>
        </div>
    );
};

const getPriceBadgeText = (price: number) => {
    return (
        <>
            From&nbsp;<Currency>{price}</Currency>
        </>
    );
};

interface ProductBadgeProps {
    color?: string;
    text: string | ReactElement;
    icon: ReactElement;
}

export const ProductBadge = ({ color = "secondary", text, icon }: ProductBadgeProps) => {
    const mediumIcon = React.cloneElement(icon, { size: icon.props.size ?? "medium" });
    return (
        <Badge color={color} size="medium" icon={mediumIcon}>
            <span className="line m-2 ml-1 whitespace-normal break-words">{text}</span>
        </Badge>
    );
};
